/**
 * La rutina de casa de Janeth (docs/Entrenamiento/yaneth-casa-y-comidas.md,
 * 26 oct 2026): solo tapete, lunes, miércoles y viernes, 30 a 40 minutos.
 * Énfasis en piernas y glúteos, con algo de torso.
 *
 * Se ofrece en Gym, en Casa, en el teléfono que se eligió como de Janeth
 * (Settings.rutinasDe) y se crea cuando ella lo confirma. Su rutina del
 * gimnasio, sus cargas y su historial no cambian. La de casa de Fernando
 * espera su revisión: no se crea.
 *
 * - Sin bandas, pesas ni banco: todos son de peso corporal, solo
 *   repeticiones. Sin carga no es un dato pendiente.
 * - Descansos de casa: 60 a 90 s entre series y hasta 120 s en los
 *   desplantes si hace falta. Como el temporizador necesita un valor, empieza
 *   con 90 s, editable. No se usan los del gimnasio.
 * - Sin biseries. Completar los dos lados cuenta como una serie y se anotan
 *   las repeticiones de cada lado (porLado).
 * - Pausa = sostener la posición: la sentadilla con pausa lleva 2 s abajo.
 *   La bajada lenta (3 s) es la velocidad de la bajada, no una pausa: no hay
 *   campo para eso y va en las indicaciones.
 * - Las alternativas (con ambos pies, contra la pared) van en las notas; el
 *   día que se usen se anotan como variante.
 * - Sin cargas iniciales: la primera vez cada ejercicio dice "Primera vez".
 */

import type { EjercicioDePlantilla, RutinaDePlantilla } from './rutinas-precargadas';

/** El descanso inicial en casa (26 oct 2026): 90 s, editable. */
export const DESCANSO_DE_JANETH_CASA_SEG = 90;

export const GENERAL_DE_JANETH_CASA =
  'Solo tapete. Calienta 4 a 5 min: camina en el sitio, sentadillas suaves, movimientos de cadera y algunos puentes. ' +
  'Descansa 60 a 90 s entre series (empieza con 90 y ajusta ±15 s); en los desplantes, hasta 120 s si hace falta. ' +
  'Los dos lados cuentan como una serie. En los principales, termina con unas 2 repeticiones posibles con buena técnica. ' +
  'Empieza en el mínimo del rango; al llegar al máximo con facilidad, progresa con pausa o una variante más difícil y anótalo. ' +
  'Detente si duele una articulación o la espalda. De 30 a 40 min, sin apresurar técnica ni descansos.';

const ejercicio = (
  exerciseId: string,
  series: number,
  repsMin: number,
  repsMax: number,
  notas: string,
  extra: Pick<EjercicioDePlantilla, 'porLado' | 'pausaSeg'> = {},
): EjercicioDePlantilla => ({
  exerciseId,
  series,
  repsMin,
  repsMax,
  descansoSeg: DESCANSO_DE_JANETH_CASA_SEG,
  biserie: null,
  descansoBiserieSeg: null,
  notas,
  ...extra,
});

const POR_LADO = { porLado: true } as const;

const DESPLANTE = 'Mantén los dos pies en su posición durante las repeticiones. Descanso de hasta 120 s si hace falta.';
const ABDUCCION = 'Eleva la pierna de arriba sin girar la pelvis ni apuntar la rodilla hacia el techo.';

export const RUTINA_DE_JANETH_CASA: readonly RutinaDePlantilla[] = [
  {
    id: 'janeth.casa.lunes',
    nombre: 'Lunes · Piernas y glúteos',
    dias: [1],
    notas: GENERAL_DE_JANETH_CASA,
    modalidad: 'casa',
    ejercicios: [
      ejercicio('ejb_desplante_estatico_sin_carga', 3, 10, 15, DESPLANTE, POR_LADO),
      ejercicio(
        'ejb_puente_gluteos_una_pierna',
        3,
        10,
        15,
        'Eleva la cadera sin girar la pelvis ni arquear la espalda. Si no la controlas, hazlo con ambos pies y anótalo como variante.',
        POR_LADO,
      ),
      ejercicio('ejb_sentadilla_pausa_abajo', 2, 12, 20, 'Pausa de 2 segundos abajo, en un recorrido controlado.', { pausaSeg: 2 }),
      ejercicio('ejb_abduccion_cadera_lado', 2, 15, 25, ABDUCCION, POR_LADO),
    ],
  },
  {
    id: 'janeth.casa.miercoles',
    nombre: 'Miércoles · Pierna, torso y abdomen',
    dias: [3],
    notas: GENERAL_DE_JANETH_CASA,
    modalidad: 'casa',
    ejercicios: [
      ejercicio(
        'ejb_sentadilla_bajada_lenta',
        3,
        12,
        20,
        'Baja en 3 segundos y sube con control. Los 3 segundos son la bajada, no una pausa.',
      ),
      ejercicio(
        'ejb_flexiones_rodillas',
        2,
        6,
        15,
        'Mantén la alineación de las rodillas a la cabeza. Alternativa: contra la pared; anótala como variante.',
      ),
      ejercicio(
        'ejb_elevaciones_w_boca_abajo',
        2,
        10,
        15,
        'Eleva un poco brazos y manos, sin levantar el pecho ni arquear la espalda. Es trabajo de espalda superior: no equivale a un remo con carga.',
      ),
      ejercicio(
        'ejb_crunch_tapete',
        2,
        10,
        15,
        'Eleva la parte alta de los hombros, sin tirar del cuello ni sentarte por completo.',
      ),
      ejercicio('ejb_pantorrilla_pie_sin_carga', 2, 15, 25, 'Con ambos pies: sube y baja con control.'),
    ],
  },
  {
    id: 'janeth.casa.viernes',
    nombre: 'Viernes · Glúteos y femorales',
    dias: [5],
    notas: GENERAL_DE_JANETH_CASA,
    modalidad: 'casa',
    ejercicios: [
      ejercicio('ejb_desplante_estatico_sin_carga', 3, 10, 15, `Misma variante del lunes. ${DESPLANTE}`, POR_LADO),
      ejercicio(
        'ejb_puente_gluteos_pies_alejados',
        3,
        10,
        20,
        'Ambos pies apoyados, con los talones más lejos que en el puente habitual y sin extender por completo las rodillas. Si da calambre, acerca los pies.',
      ),
      ejercicio(
        'ejb_puente_gluteos_una_pierna',
        2,
        10,
        15,
        'Si no controlas la pelvis, hazlo con ambos pies y anótalo como variante.',
        POR_LADO,
      ),
      ejercicio('ejb_abduccion_cadera_lado', 2, 15, 25, `Misma variante del lunes. ${ABDUCCION}`, POR_LADO),
    ],
  },
];
